import { BusMessage, MessageFactory, calcStringByteLength } from "flavr-common";
// The Message from main to worker, to give it a port to the bus
/**
 * Transfers a message bus port between execution contexts.
 */
export class BusPortMessage extends BusMessage {
    // why is RegExp not defined in worker context?
    constructor(appUid, port) {
        super();
        this.appUid = appUid;
        this.port = port;
    }
    serialization() {
        throw new Error("MessagePort can't be serialized");
    }
    static is(msg) {
        return msg.type === BusPortMessage.typestatic;
    }
}
BusPortMessage.typestatic = "BusPortMessage";
BusPortMessage.subjectstatic = "system";
/**
 * Starts a low-level bus handshake.
 */
export class HandshakeMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(appUid) {
        super();
        this.appUid = appUid;
    }
    serialization() {
        throw new Error("HandshakeMessage can't be serialized");
    }
    static is(msg) {
        return msg.type === HandshakeMessage.typestatic;
    }
}
HandshakeMessage.typestatic = "HandshakeMessage";
HandshakeMessage.subjectstatic = "system";
/**
 * Starts a module-level bus handshake.
 */
export class HandshakeModuleMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.moduleId);
    }
    constructor(moduleId) {
        super();
        this.moduleId = moduleId;
        this.type = `${HandshakeModuleMessage.typestatic}:${moduleId}`;
    }
    serialization(stream) {
        this.moduleId = stream.utf16String(this.moduleId);
    }
    static is(msg) {
        return msg.type.startsWith(`${HandshakeModuleMessage.typestatic}:`);
    }
}
HandshakeModuleMessage.typestatic = "HandshakeModuleMessage";
HandshakeModuleMessage.subjectstatic = "general";
MessageFactory.register(HandshakeModuleMessage);
/**
 * Returns the result of a module-level bus handshake.
 */
export class ResponseHandshakeModuleMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.moduleId);
    }
    constructor(moduleId) {
        super();
        this.moduleId = moduleId;
    }
    serialization(stream) {
        this.moduleId = stream.utf16String(this.moduleId);
    }
    static is(msg) {
        return msg.type === ResponseHandshakeModuleMessage.typestatic;
    }
}
ResponseHandshakeModuleMessage.typestatic = "ResponseHandshakeModuleMessage";
ResponseHandshakeModuleMessage.subjectstatic = "general";
MessageFactory.register(ResponseHandshakeModuleMessage);
// First Message to be sent from a worker to the bus defining id of client
/**
 * Requests a bus connection.
 */
export class ConnectMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(appUid) {
        super();
        this.appUid = appUid;
    }
    serialization() {
        throw new Error("ConnectMessage can't be serialized");
    }
    static is(msg) {
        return msg.type === ConnectMessage.typestatic;
    }
}
ConnectMessage.typestatic = "ConnectMessage";
ConnectMessage.subjectstatic = "system";
/**
 * Reports that a bus connection request was refused.
 */
export class ConnectionRefusedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.reason);
    }
    constructor(reason) {
        super();
        this.reason = reason;
    }
    serialization(stream) {
        this.reason = stream.utf16String(this.reason);
    }
    static is(msg) {
        return msg.type === ConnectionRefusedMessage.typestatic;
    }
}
ConnectionRefusedMessage.typestatic = "ConnectionRefusedMessage";
ConnectionRefusedMessage.subjectstatic = "system";
MessageFactory.register(ConnectionRefusedMessage);
// Subscribing to a pattern of messages
/**
 * Subscribes to bus subjects or message patterns.
 */
export class SubscribeMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(pattern, unsubscribe = false) {
        super();
        this.pattern = pattern;
        this.unsubscribe = unsubscribe;
    }
    serialization() {
        throw new Error("SubscribeMessage can't be serialized");
    }
    static is(msg) {
        return msg.type === SubscribeMessage.typestatic;
    }
}
SubscribeMessage.typestatic = "SubscribeMessage";
SubscribeMessage.subjectstatic = "system";
// Message on startup after all workers have connected to the bus, @see AbstractModule
/**
 * Announces that the message bus system initialization started.
 */
export class SystemInitMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.locationHref);
    }
    // @param the actual url to allow switches based on url parameters
    constructor(locationHref) {
        super();
        this.locationHref = locationHref;
    }
    serialization(stream) {
        this.locationHref = stream.utf16String(this.locationHref);
    }
    static is(msg) {
        return msg.type === SystemInitMessage.typestatic;
    }
}
SystemInitMessage.typestatic = "SystemInitMessage";
SystemInitMessage.subjectstatic = "system";
MessageFactory.register(SystemInitMessage);
// Message to the bus that a worker has started (after all have started, a SystemInitMessage gets sent from worker.factory)
/**
 * Announces that a worker finished starting.
 */
export class WorkerStartedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.workerId);
    }
    constructor(workerId) {
        super();
        this.workerId = workerId;
    }
    serialization(stream) {
        this.workerId = stream.utf16String(this.workerId);
    }
    static is(msg) {
        return msg.type === WorkerStartedMessage.typestatic;
    }
}
WorkerStartedMessage.typestatic = "WorkerStartedMessage";
WorkerStartedMessage.subjectstatic = "system";
MessageFactory.register(WorkerStartedMessage);
/**
 * Requests whether bus subscriptions are ready.
 */
export class RequestBusSubscriptionsReadyMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.workerId);
    }
    constructor(workerId) {
        super();
        this.workerId = workerId;
    }
    serialization(stream) {
        this.workerId = stream.utf16String(this.workerId);
    }
    static is(msg) {
        return msg.type === RequestBusSubscriptionsReadyMessage.typestatic;
    }
}
RequestBusSubscriptionsReadyMessage.typestatic = "RequestBusSubscriptionsReadyMessage";
RequestBusSubscriptionsReadyMessage.subjectstatic = "system";
MessageFactory.register(RequestBusSubscriptionsReadyMessage);
/**
 * Announces that bus subscriptions are ready.
 */
export class BusSubscriptionsReadyMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.workerId);
    }
    constructor(workerId) {
        super();
        this.workerId = workerId;
    }
    serialization(stream) {
        this.workerId = stream.utf16String(this.workerId);
    }
    static is(msg) {
        return msg.type === BusSubscriptionsReadyMessage.typestatic;
    }
}
BusSubscriptionsReadyMessage.typestatic = "BusSubscriptionsReadyMessage";
BusSubscriptionsReadyMessage.subjectstatic = "system";
MessageFactory.register(BusSubscriptionsReadyMessage);
// Message to the bus that a worker has failed (onerror) and the port shall be cleared
/**
 * Announces that a worker died.
 */
export class WorkerDiedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.workerId);
    }
    constructor(workerId) {
        super();
        this.workerId = workerId;
    }
    serialization(stream) {
        this.workerId = stream.utf16String(this.workerId);
    }
    static is(msg) {
        return msg.type === WorkerDiedMessage.typestatic;
    }
}
WorkerDiedMessage.typestatic = "WorkerDiedMessage";
WorkerDiedMessage.subjectstatic = "system";
MessageFactory.register(WorkerDiedMessage);
// Gather system stats from other modules
/**
 * Requests a system statistics snapshot.
 */
export class GatherSystemStatsMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === GatherSystemStatsMessage.typestatic;
    }
}
GatherSystemStatsMessage.typestatic = "GatherSystemStatsMessage";
GatherSystemStatsMessage.subjectstatic = "system";
MessageFactory.register(GatherSystemStatsMessage);
/**
 * The reply to GatherModuleStatsMessage
 */
export class ModuleStatsMessage extends BusMessage {
    get byteLength() {
        let byteLength = 0;
        byteLength += calcStringByteLength(this.workerId);
        byteLength += calcStringByteLength(this.moduleId);
        byteLength += Uint16Array.BYTES_PER_ELEMENT; // messageInducedWork.length
        for (const workCaused of this.messageInducedWork) {
            byteLength += calcStringByteLength(workCaused.sourceModuleId);
            byteLength += calcStringByteLength(workCaused.sourceWorkerId);
            byteLength += Uint16Array.BYTES_PER_ELEMENT; // statsByMessageType length
            for (const messageType in workCaused.statsByMessageType) {
                byteLength += calcStringByteLength(messageType);
                byteLength += Uint32Array.BYTES_PER_ELEMENT * 2; // countTotal countDelta
                byteLength += Float64Array.BYTES_PER_ELEMENT * 2; // processingTimeTotal processingTimeDelta
            }
        }
        return byteLength;
    }
    constructor(workerId = "", moduleId = "", messageInducedWork = []) {
        super();
        this.workerId = workerId;
        this.moduleId = moduleId;
        this.messageInducedWork = messageInducedWork;
    }
    serialization(stream) {
        this.workerId = stream.utf16String(this.workerId);
        this.moduleId = stream.utf16String(this.moduleId);
        if (stream.isReading)
            this.messageInducedWork = [];
        const workCausedLength = stream.uint16(this.messageInducedWork.length);
        for (let i = 0; i < workCausedLength; ++i) {
            let workCaused;
            if (stream.isReading) {
                workCaused = {
                    sourceModuleId: "",
                    sourceWorkerId: "",
                    statsByMessageType: {}
                };
                this.messageInducedWork.push(workCaused);
            }
            else {
                workCaused = this.messageInducedWork[i];
            }
            workCaused.sourceModuleId = stream.utf16String(workCaused.sourceModuleId);
            workCaused.sourceWorkerId = stream.utf16String(workCaused.sourceWorkerId);
            if (stream.isReading) {
                const statsLength = stream.uint16(0);
                for (let i = 0; i < statsLength; ++i) {
                    const messageType = stream.utf16String("");
                    const stats = {
                        countTotal: 0,
                        countDelta: 0,
                        processingTimeTotal: 0,
                        processingTimeDelta: 0
                    };
                    stats.countTotal = stream.uint32(stats.countTotal);
                    stats.countDelta = stream.uint32(stats.countDelta);
                    stats.processingTimeTotal = stream.float64(stats.processingTimeTotal);
                    stats.processingTimeDelta = stream.float64(stats.processingTimeDelta);
                    workCaused.statsByMessageType[messageType] = stats;
                }
            }
            else {
                let statsLength = 0;
                for (const _messageType in workCaused.statsByMessageType) {
                    ++statsLength;
                }
                stream.uint16(statsLength);
                for (const messageType in workCaused.statsByMessageType) {
                    const stats = workCaused.statsByMessageType[messageType];
                    stream.utf16String(messageType);
                    stats.countTotal = stream.uint32(stats.countTotal);
                    stats.countDelta = stream.uint32(stats.countDelta);
                    stats.processingTimeTotal = stream.float64(stats.processingTimeTotal);
                    stats.processingTimeDelta = stream.float64(stats.processingTimeDelta);
                }
            }
        }
    }
    static is(msg) {
        return msg.type === ModuleStatsMessage.typestatic;
    }
}
ModuleStatsMessage.typestatic = "ModuleStatsMessage";
ModuleStatsMessage.subjectstatic = "system";
MessageFactory.register(ModuleStatsMessage);
